import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faCheckCircle,
  faExclamationTriangle,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { useLingui } from '@lingui/react/macro';
import { CheckboxDropdown } from '@repo/videa-components/src';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { uniqBy } from 'lodash';

import type { ClaimWritebackStatus } from '@/react-query/useWritebackStatus';

import {
  PatientListModesMap,
  RecentVisitsSortFilter,
  SortFilterEnum,
} from '../../../@types/appointments';
import type { TClaimMessage } from '../../../@types/rcm';
import { SummaryCard } from '../../../common-components/elements/SummaryCard/SummaryCard';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { useRcmTreatmentsBatch } from '../../../react-query/rcm/useRcmTreatmentsBatch';
import { useAppointments } from '../../../react-query/useAppointments';
import type { Appointment } from '../../../react-query/useAppointments';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { useAmbientInfiniteScroll } from '../../ambient/notesPage/hooks/useAmbientInfiniteScroll';
import {
  INITIAL_SELECTED_ANALYSES_STATE,
  modeAtom,
  searchAtom,
  selectDateAtom,
  selectedAnalysesAtom,
  sortFilterAtom,
} from '../../patientList/atoms';
import { PatientListPageLayout } from '../../patientList/PatientListPageLayout';
import { LoadingSpinner } from '../../patientList/styles';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import type { TTreatmentResponse } from '../freeClaims/insuranceExport/types';

import { batchReadyPatientsAtom, batchSessionAtom } from './batchSession';
import type { BatchPatient } from './batchSession';
import { ClaimsBanner } from './ClaimsBanner';
import { ClaimsTable } from './ClaimsTable';
import {
  getClaimStatusForAppointment,
  getSortParam,
  normalizeEstimatedVisitId,
  normalizeImagingPatientId,
} from './helpers';
import { showClaimsBanner } from './state';
import { ClaimTableStatus } from './types';

export const ClaimsPage = () => {
  // Landing page for clean claims
  // state
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);
  const sortFilter = useAtomValue(sortFilterAtom);
  const selectedDate = useAtomValue(selectDateAtom);

  const setSelectedAnalysesAtom = useSetAtom(selectedAnalysesAtom);
  const setSelectedDate = useSetAtom(selectDateAtom);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showBanner, setShowBanner] = useAtom(showClaimsBanner);
  const [selectedCard, setSelectedCard] = useState<
    'allPatients' | 'ready' | 'review'
  >('allPatients');
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedServiceCodes, setSelectedServiceCodes] = useState<string[]>(
    [],
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
    ClaimTableStatus.READY,
    ClaimTableStatus.REVIEW,
  ]);
  const [selectedPayers, setSelectedPayers] = useState<string[]>([]);

  const setBatchSession = useSetAtom(batchSessionAtom);
  const setBatchReadyPatients = useSetAtom(batchReadyPatientsAtom);

  // Clear any active batch session when the list is mounted — the list view means the user
  // has left the batch flow (e.g. browser Back, direct link). Exit/Finish also clear it,
  // so this is a safety net to prevent stale batch state leaking into single-patient views.
  useEffect(() => {
    setBatchSession(null);
  }, [setBatchSession]);

  // hooks
  const { t } = useLingui();
  const { debouncedValue: debouncedSearch } = useDebounce(search);

  const isSearchValid = debouncedSearch.length > 2;
  const today = useMemo(() => new Date(), []);

  let endDate: Date | undefined = endOfDay(today);
  let startDate: Date | undefined = undefined;
  let imagingStartDate: Date | undefined;
  let imagingEndDate: Date | undefined;
  if (selectedDate) {
    endDate = endOfDay(selectedDate);
  }
  if (isSearchValid) {
    endDate = undefined;
  }
  if (selectedDate) {
    startDate = startOfDay(selectedDate);
  }
  if (sortFilter === "Today's Radiographs") {
    imagingStartDate = startOfDay(today);
    imagingEndDate = endOfDay(today);
    startDate = startOfDay(today);
  }

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAppointments({
    appointmentOpts: {
      startDate,
      endDate,
      imagingStartDate,
      imagingEndDate,
      onlyImaging: mode === PatientListModesMap.RecentVisits && !isSearchValid,
    },
    patientOpts: {
      name: isSearchValid ? debouncedSearch : undefined,
    },
    meta: {
      sort: {
        appointmentOn: getSortParam(mode, sortFilter),
      },
      pagination: {
        limit:
          mode === PatientListModesMap.DailyHuddle ||
          sortFilter === "Today's Radiographs"
            ? undefined
            : 10,
      },
    },
  });

  const hasPages = !!appointmentData?.pages?.length;
  const { ref: sentinelRef, setScrollRoot } = useAmbientInfiniteScroll(
    hasNextPage,
    isFetchingNextPage,
    hasPages,
    fetchNextPage,
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      (
        scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = node;
      setScrollRoot(node);
    },
    [setScrollRoot],
  );

  const claimSummariesTreatmentData = useRcmTreatmentsBatch(appointmentData);

  // Process treatment data incrementally - don't wait for all batches
  const claimSummariesTreatments = useMemo(() => {
    const allTreatments: any[] = [];

    claimSummariesTreatmentData?.forEach((queryResult) => {
      if (queryResult.data && Array.isArray(queryResult.data)) {
        allTreatments.push(...queryResult.data);
      }
    });

    return allTreatments;
  }, [claimSummariesTreatmentData]);

  const allAppointments = useMemo(() => {
    setSelectedAnalysesAtom(INITIAL_SELECTED_ANALYSES_STATE);
    return appointmentData?.pages.flatMap((page) => page.data);
  }, [setSelectedAnalysesAtom, appointmentData]);

  const uniqueAppointments = useMemo(() => {
    return uniqBy(
      allAppointments,
      (appointment) => appointment.appointment.unifiedId,
    );
  }, [allAppointments]);

  // Generate an array of appointments with their treatments attached
  // This will now update incrementally as treatment data becomes available
  // allTreatmentsExcluded refers to claims that have treatments that don't
  // need any attachments, and therefore don't require narrative generation/review
  // patientName, providerName, dateOfService from batch findings when available
  const filteredAppointmentsWithTreatments = useMemo<
    (Appointment & {
      treatments: TTreatmentResponse[];
      messages?: TClaimMessage[];
      allTreatmentsExcluded: boolean;
      patientName?: string;
      providerName?: string;
      dateOfService?: string;
      payerName?: string;
      payerId?: number;
      writebackStatus?: ClaimWritebackStatus;
      pushedAt?: string;
    })[]
  >(() => {
    return (
      (uniqueAppointments || [])
        .map((appointment) => {
          // Find the matching treatment data for this appointment
          const matchingTreatmentData = claimSummariesTreatments?.find(
            (treatmentData) => {
              // Match by unifiedAppointmentId
              return (
                treatmentData?.unifiedAppointmentId &&
                appointment.appointment.unifiedId &&
                treatmentData.unifiedAppointmentId ===
                  parseInt(appointment.appointment.unifiedId)
              );
            },
          );

          // Get the treatments array from the matching treatment data
          const treatmentsArray = matchingTreatmentData?.treatments || [];

          // Return a new appointment object with treatments, messages, and optional patient/provider/date from findings
          return {
            ...appointment,
            treatments: treatmentsArray,
            messages: matchingTreatmentData?.messages,
            allTreatmentsExcluded:
              matchingTreatmentData?.allTreatmentsExcluded ?? false,
            ...(matchingTreatmentData?.patientName != null && {
              patientName: matchingTreatmentData.patientName,
            }),
            ...(matchingTreatmentData?.providerName != null && {
              providerName: matchingTreatmentData.providerName,
            }),
            ...(matchingTreatmentData?.dateOfService != null && {
              dateOfService: matchingTreatmentData.dateOfService,
            }),
            ...(matchingTreatmentData?.payerName != null && {
              payerName: matchingTreatmentData.payerName,
            }),
            ...(matchingTreatmentData?.payerId != null && {
              payerId: matchingTreatmentData.payerId,
            }),
            ...(matchingTreatmentData?.writebackStatus != null && {
              writebackStatus: matchingTreatmentData.writebackStatus,
            }),
            ...(matchingTreatmentData?.pushedAt != null && {
              pushedAt: matchingTreatmentData.pushedAt,
            }),
          };
        })
        // Filter out appointments that don't have treatments
        .filter(
          (appointment) =>
            appointment.treatments && appointment.treatments.length > 0,
        )
    );
  }, [uniqueAppointments, claimSummariesTreatments]);

  const selectItems: React.ReactNode[] = [];

  Object.values(
    mode === PatientListModesMap.DailyHuddle
      ? SortFilterEnum
      : RecentVisitsSortFilter,
  ).forEach((option) =>
    selectItems.push(
      <div data-value={option} key={option}>
        {option}
      </div>,
    ),
  );

  // Check if any batch is still loading, but don't block the UI
  const isAnyBatchLoading =
    claimSummariesTreatmentData?.some((queryResult) => queryResult.isLoading) ||
    false;

  // Show loading when appointments are loading OR when we have appointments but no treatments yet
  const isLoading =
    isLoadingAppointments ||
    (!appointmentData && isAnyBatchLoading) ||
    (uniqueAppointments.length > 0 &&
      filteredAppointmentsWithTreatments.length === 0 &&
      isAnyBatchLoading);

  // Calculate summary card values
  const allClaimsCount = filteredAppointmentsWithTreatments.length;
  const attachmentsGeneratedCount = filteredAppointmentsWithTreatments.filter(
    (appointment) => !appointment.allTreatmentsExcluded,
  ).length;
  const readyToSubmitCount = filteredAppointmentsWithTreatments.filter(
    (appointment) => getClaimStatusForAppointment(appointment) === 'ready',
  ).length;
  const needsReviewCount = filteredAppointmentsWithTreatments.filter(
    (appointment) => getClaimStatusForAppointment(appointment) === 'review',
  ).length;

  // Filter appointments for table based on selected card
  const filteredAppointmentsForTable =
    selectedCard === 'allPatients'
      ? filteredAppointmentsWithTreatments
      : selectedCard === 'ready'
        ? filteredAppointmentsWithTreatments.filter(
            (appointment) =>
              getClaimStatusForAppointment(appointment) === 'ready',
          )
        : filteredAppointmentsWithTreatments.filter(
            (appointment) =>
              getClaimStatusForAppointment(appointment) === 'review',
          );

  // Derive unique filter options from the full dataset (before card filter)
  const uniqueProviders = useMemo(() => {
    const providers = filteredAppointmentsWithTreatments
      .map(
        (a) =>
          a.providerName ??
          `${a.appointment.provider?.firstName ?? ''} ${
            a.appointment.provider?.lastName ?? ''
          }`.trim(),
      )
      .filter(Boolean);
    return [...new Set(providers)].sort();
  }, [filteredAppointmentsWithTreatments]);

  const uniquePayers = useMemo(() => {
    const payers = filteredAppointmentsWithTreatments
      .map((a) => a.payerName)
      .filter((name): name is string => !!name);
    return [...new Set(payers)].sort();
  }, [filteredAppointmentsWithTreatments]);

  const uniqueServiceCodes = useMemo(() => {
    const codes = filteredAppointmentsWithTreatments.flatMap((a) =>
      a.treatments.map((tx) => tx.txCode),
    );
    return [...new Set(codes)].sort();
  }, [filteredAppointmentsWithTreatments]);

  const statusLabels: Record<string, string> = useMemo(
    () => ({
      ready: t`Ready to Submit`,
      review: t`Needs Review`,
      'no-attachment': t`No Attachment Needed`,
      submitted: t`Submitted`,
    }),
    [t],
  );
  const statusItems = useMemo(() => Object.keys(statusLabels), [statusLabels]);

  // Apply dropdown filters after card filter
  const finalFilteredAppointments = useMemo(() => {
    return filteredAppointmentsForTable.filter((appointment) => {
      if (selectedProviders.length > 0) {
        const provName =
          appointment.providerName ??
          `${appointment.appointment.provider?.firstName ?? ''} ${
            appointment.appointment.provider?.lastName ?? ''
          }`.trim();
        if (!selectedProviders.includes(provName)) return false;
      }
      if (selectedServiceCodes.length > 0) {
        const codes = appointment.treatments.map((tx) => tx.txCode);
        if (!selectedServiceCodes.some((c) => codes.includes(c))) return false;
      }
      if (selectedStatuses.length > 0) {
        const status = getClaimStatusForAppointment(appointment);
        if (!selectedStatuses.includes(status)) return false;
      }
      if (selectedPayers.length > 0) {
        if (
          !appointment.payerName ||
          !selectedPayers.includes(appointment.payerName)
        )
          return false;
      }
      return true;
    });
  }, [
    filteredAppointmentsForTable,
    selectedProviders,
    selectedServiceCodes,
    selectedStatuses,
    selectedPayers,
  ]);

  // Collect ready-to-submit appointments from the current visible table for batch review
  const batchReadyPatients = useMemo<BatchPatient[]>(() => {
    return filteredAppointmentsWithTreatments
      .filter((a) =>
        ['ready', 'review'].includes(getClaimStatusForAppointment(a)),
      )
      .map((a) => ({
        unifiedId: a.appointment.unifiedId,
        estimatedVisitId: normalizeEstimatedVisitId(
          a.appointment.estimatedVisitId,
        ),
        imagingPatientId: normalizeImagingPatientId(
          a.patient?.imagingPatientVideaId,
        ),
      }));
  }, [filteredAppointmentsWithTreatments]);

  const dateLocale = useGetDateFNSLocale();
  const dateFormatted =
    selectedDate &&
    format(selectedDate, 'MMMM d, yyyy', {
      locale: dateLocale,
    });

  // Calculate time saved based on number of appointments (7 minutes per appointment)
  const calculateTimeSaved = (appointmentCount: number): string => {
    const totalMinutes = appointmentCount * 7;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourSuffix = hours !== 1 ? 's' : '';
    const minuteSuffix = minutes !== 1 ? 's' : '';

    if (hours > 0 && minutes > 0) {
      return t`~${hours} hour${hourSuffix} ${minutes} minute${minuteSuffix}`;
    } else if (hours > 0) {
      return t`~${hours} hour${hourSuffix}`;
    } else {
      return t`~${minutes} minute${minuteSuffix}`;
    }
  };

  const timeSavedString = calculateTimeSaved(attachmentsGeneratedCount);

  // useEffects
  useEffect(() => {
    setBatchReadyPatients(batchReadyPatients);
    return () => setBatchReadyPatients([]);
  }, [batchReadyPatients, setBatchReadyPatients]);

  useEffect(() => {
    if (!search && !selectedDate) {
      setSelectedDate(today);
    }
  }, [search, selectedDate, setSelectedDate, today]);

  // Reset dropdown filters when the card filter, search context, or date changes
  useEffect(() => {
    setSelectedProviders([]);
    setSelectedServiceCodes([]);
    setSelectedStatuses([ClaimTableStatus.READY, ClaimTableStatus.REVIEW]);
    setSelectedPayers([]);
    setSelectedRows([]);
  }, [selectedCard, isSearchValid, selectedDate]);

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.Claims}
      allAppointments={filteredAppointmentsWithTreatments}
      isLoadingAppointments={
        isLoadingAppointments ||
        (!appointmentData && isAnyBatchLoading) ||
        (uniqueAppointments.length > 0 &&
          filteredAppointmentsWithTreatments.length === 0 &&
          isAnyBatchLoading)
      }
      isLoadingVisitSummaries={isAnyBatchLoading}
    >
      <div className="vai-flex vai-flex-col vai-h-full vai-overflow-hidden">
        {showBanner && !isLoading && !isSearchValid && (
          <div className="vai-shrink-0">
            <ClaimsBanner
              timeSaved={timeSavedString}
              date={dateFormatted}
              onClose={() => setShowBanner(false)}
            />
          </div>
        )}
        {!isLoading && !isSearchValid && (
          <div className="vai-space-y-4 vai-mb-6">
            <div className="vai-w-full vai-flex vai-items-stretch vai-gap-4 vai-justify-between">
              <div className="vai-flex-1 vai-min-w-0">
                <SummaryCard
                  faIcon={faUserGroup}
                  value={allClaimsCount}
                  label={t`ALL PATIENTS`}
                  description={t`patients were scheduled for dental services today`}
                  color="blue"
                  onClick={() => setSelectedCard('allPatients')}
                  testId="claims-all-patients-card"
                />
              </div>
              <div className="vai-flex-1 vai-min-w-0">
                <SummaryCard
                  faIcon={faExclamationTriangle}
                  value={needsReviewCount}
                  label={t`NEEDS REVIEW`}
                  description={t`claim attachments have warnings and need manual review`}
                  color="yellow"
                  onClick={() => setSelectedCard('review')}
                  testId="claims-review-card"
                />
              </div>
              <div className="vai-flex-1 vai-min-w-0">
                <SummaryCard
                  faIcon={faCheckCircle}
                  value={readyToSubmitCount}
                  label={t`READY TO SUBMIT`}
                  description={t`claim attachments have no warnings and are ready to submit`}
                  color="green"
                  onClick={() => setSelectedCard('ready')}
                  testId="claims-ready-card"
                />
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="vai-flex vai-justify-center vai-items-center vai-flex-1">
            <LoadingSpinner className="vai-m-8" />
          </div>
        ) : (
          <div className="vai-flex vai-flex-1 vai-flex-col vai-min-h-0 vai-overflow-hidden">
            <div className="vai-flex vai-items-center vai-gap-2 vai-mb-4 vai-flex-shrink-0">
              <CheckboxDropdown
                items={uniqueProviders}
                selectedItems={selectedProviders}
                onChange={setSelectedProviders}
                getItemLabel={(p) => p}
                getItemValue={(p) => p}
                placeholder={t`Provider`}
                includeAllOption
                type="claims-provider"
              />
              <CheckboxDropdown
                items={uniquePayers}
                selectedItems={selectedPayers}
                onChange={setSelectedPayers}
                getItemLabel={(p) => p}
                getItemValue={(p) => p}
                placeholder={t`Primary Payer`}
                includeAllOption
                type="claims-payer"
              />
              <CheckboxDropdown
                items={uniqueServiceCodes}
                selectedItems={selectedServiceCodes}
                onChange={setSelectedServiceCodes}
                getItemLabel={(c) => c}
                getItemValue={(c) => c}
                placeholder={t`Service Codes`}
                includeAllOption
                type="claims-service-codes"
              />
              <CheckboxDropdown
                items={statusItems}
                selectedItems={selectedStatuses}
                onChange={setSelectedStatuses}
                getItemLabel={(s) => statusLabels[s] ?? s}
                getItemValue={(s) => s}
                placeholder={t`Status`}
                includeAllOption
                type="claims-status"
              />
            </div>
            {isLoadingAppointments ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-flex-1">
                <LoadingSpinner className="vai-m-8" />
              </div>
            ) : (
              <>
                <ClaimsTable
                  appointments={finalFilteredAppointments}
                  selectedRows={selectedRows}
                  isSearchActive={isSearchValid}
                  onSetSelectedRows={setSelectedRows}
                  scrollContainerRef={scrollContainerCallbackRef}
                  sentinelRef={sentinelRef}
                />
                {isFetchingNextPage &&
                  filteredAppointmentsWithTreatments?.length !== 0 && (
                    <LoadingSpinner />
                  )}
              </>
            )}
          </div>
        )}
      </div>
    </PatientListPageLayout>
  );
};
